/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * testPhoneNumbers — dev/test allow-list for Firebase "fictional" phone numbers.
 *
 * Firebase Auth lets you register fixed test numbers (Console → Authentication →
 * Sign-in method → Phone → "Phone numbers for testing") that return a canned
 * OTP, send NO real SMS, and DON'T count against the anti-abuse limit. That's
 * the only way to log in/out repeatedly with the same number without tripping
 * `auth/too-many-requests`.
 *
 * On the native iOS path the OTP send is gated on APNs readiness (waitForApns)
 * so the silent-push verification works. A test number on a simulator (no APNs)
 * would stall that gate, so the sheet checks `isTestPhoneNumber` to SKIP the
 * gate and route straight through the web verifier, which honors test numbers.
 *
 * The list comes from `VITE_TEST_PHONE_NUMBERS` (comma-separated E.164). It is
 * empty in production builds, so `isTestPhoneNumber` always returns false there
 * — this is strictly a dev/test convenience and never changes prod behavior.
 */

/** Parse the comma-separated env value into a normalized E.164 set. */
export function parseTestPhoneNumbers(raw: string | undefined): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.startsWith("+")),
  );
}

/**
 * Is `e164` a registered Firebase test number (per `VITE_TEST_PHONE_NUMBERS`)?
 * `allowList` is injectable for tests; defaults to the env-derived set.
 */
export function isTestPhoneNumber(
  e164: string,
  allowList: Set<string> = ENV_TEST_NUMBERS,
): boolean {
  return allowList.has(e164);
}

// Resolved once from the build-time env. Empty unless explicitly configured.
export const ENV_TEST_NUMBERS: Set<string> = parseTestPhoneNumbers(
  import.meta.env?.VITE_TEST_PHONE_NUMBERS,
);
